import * as React from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  Link,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { userApi } from "../../apiServices/UserApiService";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // 👇 Call your API with email
      const res = await userApi.forgotPassword({ email });

      // ✅ Success response
      setSnackbar({
        open: true,
        message: res?.message || "Password reset link sent to your email.",
        severity: "success",
      });
    } catch (error: any) {
      // ❌ API error
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Failed to send reset link. Try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f5f7fb",
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            textAlign: "center",
          }}
        >
          {/* Logo (optional) */}
          <Box sx={{ mb: 2 }}>
            <img src="/logo192.png" alt="Company Logo" style={{ height: 40 }} />
          </Box>

          <Typography variant="h5" fontWeight={700} gutterBottom>
            Forgot your password
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
            Please enter the email address you'd like your password reset
            information sent to
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <TextField
              label="Enter email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                py: 1.2,
                fontWeight: 600,
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              {loading ? "Sending..." : "Request reset link"}
            </Button>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Link
              component="button"
              underline="hover"
              color="primary"
              onClick={() => navigate("/")}
            >
              Back to Login
            </Link>
          </Box>
        </Paper>
      </Container>

      {/* Snackbar for messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
